import type {ReactElement} from 'react';
import {useContext, useEffect, useState} from 'react';
import type {OverlayChildren} from 'react-bootstrap/Overlay';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {IconWithTooltip} from '@Components/icon-with-tooltip/icon-with-tooltip';
import {TooltipPlacement} from '@Components/icon-with-tooltip/icon-with-tooltip.types';
import {Text, TextSize} from '@Components/text/text';
import {Tooltip, TOOLTIP_POSITION, TOOLTIP_TYPE} from '@Components/tooltip';
import {EditorViewport} from '@FormMakerReact/form-maker.types';
import {formMakerContext} from '@FormMakerReact/form-maker.context';
import {useRelativeTimeTick} from '@FormMakerReact/layout/form-maker-header/feature-hooks';
import type {SaveBadgeProps} from './save-badge.types';
import {SaveStateKind} from './save-badge.types';
import {getRelativeSavedTooltip, getSaveStateKind} from './save-badge.util';
import styles from '@FormMakerReact/layout/form-maker-header/form-maker-header.module.scss';

export function SaveBadge({state, onRetry}: SaveBadgeProps): ReactElement {
  const {state: contextState, store} = useContext(formMakerContext);
  const {editorViewport} = contextState;
  const kind = getSaveStateKind(state, {isDraft: !store.canAutosave});
  const [isSaveErrorTooltipShown, setIsSaveErrorTooltipShown] = useState(false);
  useRelativeTimeTick();

  useEffect(() => {
    setIsSaveErrorTooltipShown(kind === SaveStateKind.FAILED);
  }, [kind]);

  if (kind === SaveStateKind.FAILED) {
    return renderFailedBadge(onRetry, editorViewport, state.saveErrorMessage, isSaveErrorTooltipShown);
  }
  if (kind === SaveStateKind.SAVING) {
    return renderNeutralBadge('icon-save-loading', window.i18next.t('pmwjs_saving'), editorViewport);
  }
  if (kind === SaveStateKind.UNSAVED) {
    return renderNeutralBadge('icon-offline', window.i18next.t('pmwjs_form_maker_unsaved_changes'), editorViewport);
  }
  if (kind === SaveStateKind.AUTOSAVE_OFF) {
    return renderNeutralBadge('icon-offline', window.i18next.t('pmwjs_signup_form_autosave_disabled_description'), editorViewport, window.i18next.t('pmwjs_disabled'));
  }
  return renderNeutralBadge('icon-auto-saved', getRelativeSavedTooltip(state.lastSavedAt), editorViewport, window.i18next.t('pmwjs_all_changes_saved_without_period'));
}

function renderText(label: string, editorViewport: EditorViewport): ReactElement | null {
  if (editorViewport !== EditorViewport.DESKTOP) {
    return null;
  }
  return <Text val={label} size={TextSize.SM} className={styles.saveBadgeText} />;
}

function renderNeutralBadge(icon: string, tooltip: string, editorViewport: EditorViewport, label: string = tooltip): ReactElement {
  return (
    <span className={styles.saveBadge}>
      <IconWithTooltip
        icon={icon}
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST_UNCOLORED}
        className={`${styles.saveBadgeIcon} ${styles.saveBadgeIconNeutral}`}
        tooltipText={tooltip}
        ariaLabel={tooltip}
        placement={TooltipPlacement.BOTTOM}
      />
      {renderText(label, editorViewport)}
    </span>
  );
}

function renderFailedBadge(onRetry: () => Promise<boolean>, editorViewport: EditorViewport, saveErrorMessage: string | null, isSaveErrorTooltipShown: boolean): ReactElement {
  const badge = renderFailedBadgeContent(onRetry, editorViewport);
  if (!saveErrorMessage) {
    return badge;
  }

  return (
    <OverlayTrigger show={isSaveErrorTooltipShown} trigger={[]} placement={TooltipPlacement.BOTTOM} overlay={renderSaveErrorTooltip(saveErrorMessage)}>
      {badge}
    </OverlayTrigger>
  );
}

function renderFailedBadgeContent(onRetry: () => Promise<boolean>, editorViewport: EditorViewport): ReactElement {
  const label = window.i18next.t('pmwjs_not_saved_retry');
  return (
    <span className={`${styles.saveBadge} ${styles.saveBadgeError}`}>
      <Icon
        icon="icon-offline"
        size={IconSize.SIZE_ICON_16}
        type={IconType.GHOST_UNCOLORED}
        className={`${styles.saveBadgeIcon} ${styles.saveBadgeIconError}`}
        ariaLabel={label}
      />
      {renderText(label, editorViewport)}
      <button
        type="button"
        className={styles.saveBadgeRetry}
        aria-label={window.i18next.t('pmwjs_retry')}
        onClick={(): void => {
          void onRetry();
        }}
      >
        <Text val={window.i18next.t('pmwjs_retry')} size={TextSize.SM} className={styles.saveBadgeRetryText} />
      </button>
    </span>
  );
}

function renderSaveErrorTooltip(saveErrorMessage: string): OverlayChildren {
  return (
    <Tooltip
      id="form-maker-save-error-tooltip"
      className={styles.saveErrorTooltip}
      position={TOOLTIP_POSITION.TOP_MIDDLE}
      type={TOOLTIP_TYPE.DEFAULT}
      text={<Text val={saveErrorMessage} size={TextSize.SM} />}
    />
  );
}
